import { useAuth } from '@/components/auth/authProvider';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetOrganizationStatusQuery } from '@/services/organizations';
import { Alert, AlertVariant } from '@palmetto/palmetto-components';
import { useMatch } from 'react-router-dom';

import { OrganizationStatus, OrganizationStatusLabels } from 'types';

const getOrganizationStatusAlertInfo = (status?: OrganizationStatus) => {
  switch (status) {
    case OrganizationStatus.noNewSales:
      return {
        variant: 'warning',
        message:
          'This organization has been set to No New Sales. Existing contracted Accounts can move forward, but uncontracted accounts are frozen.',
      };
    case OrganizationStatus.offBoarded:
      return {
        variant: 'danger',
        message:
          'This organization has been Offboarded from LightReach. Users are deactivated, and no action can be taken in the LightReach Portal.',
      };
    case OrganizationStatus.readOnly:
      return {
        variant: 'danger',
        message:
          'This organization has been set to Read Only. All Accounts are frozen in their current state, no information can be updated, and no Accounts can move forward.',
      };
    default:
      return null;
  }
};

export const OrganizationStatusBanner = () => {
  const { claims } = useAuth();
  const accountMatch = useMatch('/accounts/:id');
  const orgSettingsMatch = useMatch('/settings/organizations/:alias/*');
  const accountId = accountMatch?.params.id;
  const orgAlias = orgSettingsMatch?.params.alias;

  // Default the org status to the claim value
  let orgStatus = claims?.orgStatus;

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(accountId as string, {
    skip: !accountId,
  });
  // this is subtle, but if you have previously loaded an account and then navigate to the org settings page, the account object will still contain the previously loaded account
  // which might not be what you want. As a result, the status lookup below evaluates orgAlias first, then the account org.
  const { data, isLoading: isOrgStatusLoading } = useGetOrganizationStatusQuery(
    orgAlias || account?.organizationId,
    { skip: !account?.organizationId && !orgAlias },
  );

  // If we have an account or an org context then the orgStatus should be based upon the value loaded above, otherwise it should default to the claims value.
  if (accountId || orgAlias) {
    orgStatus = data?.status;
  }

  if (!isAccountLoading && !isOrgStatusLoading) {
    const organizationStatusAlertInfo = getOrganizationStatusAlertInfo(orgStatus);
    if (!organizationStatusAlertInfo) return null;
    return (
      <Alert
        margin={{ base: '0 md 0', tablet: '0' }}
        variant={organizationStatusAlertInfo.variant as AlertVariant}
        title={`Organization Status: ${OrganizationStatusLabels[orgStatus as OrganizationStatus]}`}
        message={organizationStatusAlertInfo.message}
        hasIcon
      />
    );
  } else {
    return null;
  }
};
